import React, { useEffect } from 'react';
import { motion, useAnimation, Variants } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LucideIcon } from 'lucide-react';

interface TechItem {
  name: string;
  level: number; // 0-100
  icon: React.ReactElement<{ className?: string }>;
}

interface TechStackProps {
  items: TechItem[];
  title: string;
  description?: string;
}

export const TechStack = ({ items, title, description }: TechStackProps) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const container: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const item: Variants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className="w-full">
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-primary to-accent">
          {title}
        </h2>
        {description && (
          <p className="text-muted-foreground max-w-2xl mx-auto">
            {description}
          </p>
        )}
      </div>
      
      <motion.div
        ref={ref}
        variants={container}
        initial="hidden"
        animate={controls}
        className="grid grid-cols-1 md:grid-cols-2 gap-6"
      >
        {items.map((tech, index) => {
          const IconComponent = tech.icon.type as React.FC<{ className?: string }>;
          
          return (
            <motion.div
              key={tech.name}
              variants={item}
              className="bg-card rounded-xl p-6 border border-border/50 hover:border-primary/30 transition-colors duration-300"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 flex items-center justify-center text-primary">
                    {React.cloneElement(tech.icon, { className: 'w-5 h-5' })}
                  </div>
                  <span className="font-medium">{tech.name}</span>
                </div>
                <span className="text-sm text-muted-foreground">{tech.level}%</span>
              </div>
              
              <div className="w-full bg-muted rounded-full h-2.5 overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={inView ? { width: `${tech.level}%` } : { width: 0 }}
                  transition={{ duration: 1, delay: 0.2 + index * 0.1 }}
                  className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                />
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

export default TechStack;
